import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toUrl } from "../../lib/config";
import "../../assets/styles/dynamic-career-form.scss";

/* ---------- Tipler ---------- */
type Gender = "Kadın" | "Erkek" | "Diğer";
type YesNo = "Var" | "Yok";
type Marital = "Bekar" | "Evli" | "Boşanmış" | "Dul";
type Military = "Yaptı" | "Yapmadı" | "Tecilli";

type Experience = {
  id: string;
  company: string;
  role: string;
  start: string;
  end: string;
  reason: string;
};

type Education = {
  level: string; // Bitirdiğiniz okul
  schoolName: string;
  department: string;
  graduationDate: string;
};

type FormState = {
  positions: [string, string, string];

  fullName: string;
  nationalId: string;
  birthPlace: string;
  birthDate: string;
  gender: Gender;

  phoneMobile: string;

  email: string;

  address: string;
  maritalStatus: Marital;
  childrenCount: string;

  smoking: YesNo;
  alcohol: YesNo;

  military: Military;
  lastDeferDate: string; // Tecilli ise

  hasDriverLicense: YesNo;
  driverClass: string;

  foreignLanguages: string;
  computerPrograms: string;
  references: string;

  education: Education;

  experiences: Experience[];

  cv?: File | null;
  consent: boolean;
  kvkkConsent: boolean;
};

/* ---------- Yardımcılar ---------- */
const makeId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const newExperience = (): Experience => ({
  id: makeId(),
  company: "",
  role: "",
  start: "",
  end: "",
  reason: "",
});

const initialState: FormState = {
  positions: ["", "", ""],

  fullName: "",
  nationalId: "",
  birthPlace: "",
  birthDate: "",
  gender: "Kadın",

  phoneMobile: "",

  email: "",

  address: "",
  maritalStatus: "Bekar",
  childrenCount: "",

  smoking: "Var",
  alcohol: "Var",

  military: "Yaptı",
  lastDeferDate: "",

  hasDriverLicense: "Var",
  driverClass: "",

  foreignLanguages: "",
  computerPrograms: "",
  references: "",

  education: {
    level: "",
    schoolName: "",
    department: "",
    graduationDate: "",
  },

  experiences: [newExperience()], // en az bir kartla başlasın
  cv: null,
  consent: false,
  kvkkConsent: false,
};

export default function ApplicationForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [cvError, setCvError] = useState<string | null>(null);
  const [phoneMobileError, setPhoneMobileError] = useState<string | null>(null);

  const [emailError, setEmailError] = useState<string | null>(null);

  const canAddExp = useMemo(
    () => form.experiences.length < 10,
    [form.experiences.length]
  );

  // Genel alan güncelle
  const setField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  // Pozisyon alanları
  const setPosition = (index: 0 | 1 | 2, value: string) => {
    setForm((s) => {
      const copy = [...s.positions] as [string, string, string];
      copy[index] = value;
      return { ...s, positions: copy };
    });
  };

  // Eğitim alanları
  const setEduField = <K extends keyof Education>(
    key: K,
    value: Education[K]
  ) => {
    setForm((s) => ({ ...s, education: { ...s.education, [key]: value } }));
  };

  // Deneyim alanları
  const addExperience = () => {
    if (!canAddExp) return;
    setForm((s) => ({ ...s, experiences: [...s.experiences, newExperience()] }));
  };

  const removeExperience = (id: string) => {
    setForm((s) => ({
      ...s,
      experiences: s.experiences.filter((e) => e.id !== id),
    }));
  };

  const setExperience = <K extends keyof Experience>(
    id: string,
    key: K,
    value: Experience[K]
  ) => {
    setForm((s) => ({
      ...s,
      experiences: s.experiences.map((e) =>
        e.id === id ? { ...e, [key]: value } : e
      ),
    }));
  };

  // CV: SADECE PDF
  const onFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0] ?? null;

    if (!file) {
      setField("cv", null);
      setCvError(null);
      return;
    }

    const isPdf =
      file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf) {
      setField("cv", null);
      setCvError("Yalnızca PDF dosyası yükleyebilirsiniz.");
      e.currentTarget.value = ""; // yanlış seçim temizlensin
      return;
    }

    setCvError(null);
    setField("cv", file);
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error'; text: string }>({ show: false, type: 'success', text: '' });

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget as HTMLFormElement | null;

    if (cvError) {
      alert("Lütfen geçerli bir PDF dosyası yükleyiniz.");
      return;
    }
    if (form.email && emailError) {
      alert("Lütfen geçerli bir e-posta adresi giriniz.");
      return;
    }
    setSubmitError(null);
    setSubmitOk(false);

    const payload = new FormData();
    payload.append("positions", JSON.stringify(form.positions));
    payload.append("fullName", form.fullName);
    payload.append("nationalId", form.nationalId);
    payload.append("birthPlace", form.birthPlace);
    payload.append("birthDate", form.birthDate);
    payload.append("gender", form.gender);

    payload.append("phone", form.phoneMobile);

    payload.append("email", form.email);

    payload.append("address", form.address);
    payload.append("maritalStatus", form.maritalStatus);
    payload.append("childrenCount", form.childrenCount);

    payload.append("smoking", form.smoking);
    payload.append("alcohol", form.alcohol);

    payload.append("military", form.military);
    payload.append("lastDeferDate", form.lastDeferDate);

    payload.append("hasDriverLicense", form.hasDriverLicense);
    payload.append("driverClass", form.driverClass);

    payload.append("foreignLanguages", form.foreignLanguages);
    payload.append("computerPrograms", form.computerPrograms);
    payload.append("references", form.references);

    payload.append("education", JSON.stringify(form.education));
    payload.append("experiences", JSON.stringify(form.experiences));
    payload.append("consent", String(form.consent));
    payload.append("kvkkConsent", String(form.kvkkConsent));
    if (form.cv) payload.append("cv", form.cv);

    try {
      setSubmitting(true);
      // Not: Endpoint adı daha önce yorumda /api/careers olarak belirtilmişti.
      // Resimdeki endpoint farklıysa, sadece aşağıdaki yolu değiştirmeniz yeterli.
      const res = await fetch(toUrl("/api/careers"), {
        method: "POST",
        body: payload,
      });
      // Explicit success statuses
      if (!(res.status === 200 || res.status === 201 || res.status === 204 || res.ok)) {
        // Try to extract server error
        const ct = res.headers.get('content-type') || '';
        let msg = '';
        try {
          if (ct.includes('application/json')) {
            const j = await res.json();
            msg = j?.message || j?.error || JSON.stringify(j);
          } else {
            msg = await res.text();
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg || `submit_failed_${res.status}`);
      }
      setSubmitOk(true);
      setToast({ show: true, type: 'success', text: 'Başvurunuz başarıyla alındı. Teşekkür ederiz.' });
      window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2500);
      // İsteğe bağlı: formu sıfırla
      setForm(initialState);
      if (formEl && typeof formEl.reset === 'function') {
        formEl.reset();
      }
      return; // ensure we do not execute catch logic after success
    } catch (err: any) {
      console.error("career_form_submit_error", err);
      const msg = (err?.message || '').trim();
      setSubmitError(msg || "Gönderim başarısız oldu");
      setToast({ show: true, type: 'error', text: msg || 'Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.' });
      window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="career-apply">
      {toast.show && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: toast.type === 'success' ? '#10b981' : '#ef4444',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 9999,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {toast.text}
        </div>
      )}
      <form className="ca-wrap" onSubmit={onSubmit}>
        {/* Başlık */}
        <header className="ca-head">
          <h1 className="ca-title">Başvuru</h1>
          <span className="ca-underline" />
        </header>

        {/* Çalışmak istediğiniz pozisyonlar (max 3) */}
        <div className="ca-block">
          <label className="ca-label">
            Çalışmak istediğiniz meslek-pozisyon (en fazla 3 tane yazınız)?
          </label>
          <div className="row">
            <div className="col-12 field">
              <input
                value={form.positions[0]}
                onChange={(e) => setPosition(0, e.target.value)}
                placeholder="1. Pozisyon"
              />
            </div>
            <div className="col-12 field">
              <input
                value={form.positions[1]}
                onChange={(e) => setPosition(1, e.target.value)}
                placeholder="2. Pozisyon"
              />
            </div>
            <div className="col-12 field">
              <input
                value={form.positions[2]}
                onChange={(e) => setPosition(2, e.target.value)}
                placeholder="3. Pozisyon"
              />
            </div>
          </div>
        </div>

        {/* Kimlik Bilgileri */}
        <div className="ca-block">
          <div className="row">
            <div className="col-12 field">
              <label>Adınız Soyadınız:</label>
              <input
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
              />
            </div>

            <div className="col-8 field">
              <label>TC No:</label>
              <input
                value={form.nationalId}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setField("nationalId", value);
                }}
                inputMode="numeric"
                maxLength={11}
              />
            </div>
            <div className="col-8 field" />

            <div className="col-12 field">
              <label>Doğum Yeri ve Tarihi</label>
              <input
                value={form.birthPlace}
                onChange={(e) => setField("birthPlace", e.target.value)}
                placeholder="Doğum Yeri"
              />
            </div>
            <div className="col-8 field">
              <input
                type="date"
                value={form.birthDate}
                onChange={(e) => setField("birthDate", e.target.value)}
              />
            </div>
            <div className="col-8 field">
              <select
                value={form.gender}
                onChange={(e) => setField("gender", e.target.value as Gender)}
              >
                <option>Kadın</option>
                <option>Erkek</option>
                <option>Diğer</option>
              </select>
            </div>
          </div>
        </div>

        {/* İletişim */}
        <div className="ca-block">
          <div className="row">
            <div className="col-12 field">
              <label>Telefon (Cep)</label>
              <input
                value={form.phoneMobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setField("phoneMobile", value);
                  if (value.length > 0 && !value.startsWith("0")) {
                    setPhoneMobileError("Telefon numarası 0 ile başlamalıdır");
                  } else {
                    setPhoneMobileError(null);
                  }
                }}
                inputMode="tel"
                maxLength={11}
              />
              {phoneMobileError && (
                <span style={{ color: "red", fontSize: "0.875rem", marginTop: "4px", display: "block" }}>
                  {phoneMobileError}
                </span>
              )}
            </div>
            <div className="col-12 field">
              <label>E-posta</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  setField("email", value);
                  if (value.length === 0) {
                    setEmailError(null);
                    return;
                  }
                  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
                  if (!re.test(value)) {
                    setEmailError("Lütfen geçerli bir e-posta adresi giriniz");
                  } else {
                    setEmailError(null);
                  }
                }}
                placeholder="ornek@eposta.com"
                pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
                title="Lütfen geçerli bir e-posta adresi giriniz"
              />
              {emailError && (
                <span style={{ color: "red", fontSize: "0.875rem", marginTop: "4px", display: "block" }}>
                  {emailError}
                </span>
              )}
            </div>
  
            <div className="col-12 field">
              <label>Adres</label>
              <textarea
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Medeni Hal & Alışkanlıklar */}
        <div className="ca-block">
          <div className="row">
            <div className="col-12 field">
              <label>Medeni Hal</label>
              <select
                value={form.maritalStatus}
                onChange={(e) =>
                  setField("maritalStatus", e.target.value as Marital)
                }
              >
                <option>Bekar</option>
                <option>Evli</option>
                <option>Boşanmış</option>
                <option>Dul</option>
              </select>
            </div>

            <div className="col-8 field">
              <label>Varsa Çocuk Sayısı</label>
              <input
                value={form.childrenCount}
                onChange={(e) =>
                  setField("childrenCount", e.target.value.replace(/\D/g, ""))
                }
                inputMode="numeric"
              />
            </div>

            <div className="col-8 field">
              <label>Sigara Kullanımı</label>
              <select
                value={form.smoking}
                onChange={(e) => setField("smoking", e.target.value as YesNo)}
              >
                <option>Var</option>
                <option>Yok</option>
              </select>
            </div>

            <div className="col-8 field">
              <label>Alkol Kullanımı</label>
              <select
                value={form.alcohol}
                onChange={(e) => setField("alcohol", e.target.value as YesNo)}
              >
                <option>Var</option>
                <option>Yok</option>
              </select>
            </div>
          </div>
        </div>

        {/* Askerlik, Ehliyet, Diller vs. */}
        <div className="ca-block">
          <div className="row">
            <div className="col-12 field">
              <label>Askerlik Durumu</label>
              <select
                value={form.military}
                onChange={(e) =>
                  setField("military", e.target.value as Military)
                }
              >
                <option>Yaptı</option>
                <option>Yapmadı</option>
                <option>Tecilli</option>
              </select>
            </div>

            {form.military === "Tecilli" && (
              <div className="col-12 field">
                <label>Tecilli ise Son Tecil Tarihi</label>
                <input
                  type="date"
                  value={form.lastDeferDate}
                  onChange={(e) => setField("lastDeferDate", e.target.value)}
                />
              </div>
            )}

            <div className="col-12 field">
              <label>Sürücü Belgeniz var mı?</label>
              <select
                value={form.hasDriverLicense}
                onChange={(e) =>
                  setField("hasDriverLicense", e.target.value as YesNo)
                }
              >
                <option>Var</option>
                <option>Yok</option>
              </select>
            </div>

            {form.hasDriverLicense === "Var" && (
              <div className="col-12 field">
                <label>(Varsa) Sınıf?</label>
                <input
                  value={form.driverClass}
                  onChange={(e) => setField("driverClass", e.target.value)}
                  placeholder="Örn: B, A2, SRC vb."
                />
              </div>
            )}

            <div className="col-12 field">
              <label>Bildiginiz Yabancı Diller</label>
              <input
                value={form.foreignLanguages}
                onChange={(e) => setField("foreignLanguages", e.target.value)}
                placeholder="Örn: İngilizce (İyi), Almanca (Orta)..."
              />
            </div>

            <div className="col-12 field">
              <label>Bilgisayarda kullandığınız programlar</label>
              <textarea
                value={form.computerPrograms}
                onChange={(e) =>
                  setField("computerPrograms", e.target.value)
                }
                placeholder="MS Office, Photoshop, Netsis vb."
              />
            </div>

            <div className="col-12 field">
              <label>Referanslar</label>
              <textarea
                value={form.references}
                onChange={(e) => setField("references", e.target.value)}
                placeholder="Ad Soyad - Kurum - Telefon"
              />
            </div>
          </div>
        </div>

        {/* Öğrenim Durumu */}
        <div className="ca-block">
          <h3 className="ca-subtitle">Öğrenim Durumunuz</h3>
          <div className="row">
            <div className="col-12 field">
              <label>Bitirdiğiniz Okul</label>
              <input
                value={form.education.level}
                onChange={(e) => setEduField("level", e.target.value)}
                placeholder="Lise / Ön Lisans / Lisans / Y.Lisans / Doktora ..."
              />
            </div>
            <div className="col-12 field">
              <label>Okulun Adı</label>
              <input
                value={form.education.schoolName}
                onChange={(e) => setEduField("schoolName", e.target.value)}
              />
            </div>
            <div className="col-12 field">
              <label>Bölüm</label>
              <input
                value={form.education.department}
                onChange={(e) => setEduField("department", e.target.value)}
              />
            </div>
            <div className="col-12 field">
              <label>Bitirme Tarihi</label>
              <input
                type="date"
                value={form.education.graduationDate}
                onChange={(e) =>
                  setEduField("graduationDate", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        {/* İş Deneyimleri – Dinamik */}
        <div className="ca-block">
          <div className="ca-block-head">
            <h3 className="ca-subtitle">İş Deneyiminiz</h3>
            <button
              type="button"
              className="btn-outline"
              onClick={addExperience}
              disabled={!canAddExp}
              aria-disabled={!canAddExp}
              title="Yeni deneyim ekle"
            >
              + Deneyim Ekle
            </button>
          </div>

          {form.experiences.map((exp, idx) => (
            <div className="row exp-card" key={exp.id}>
              <div className="exp-card__head">
                <div className="exp-card__title">
                  <span className="badge">{idx + 1}</span> İş Yeri
                </div>
                {form.experiences.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeExperience(exp.id)}
                    title="Bu deneyimi sil"
                  >
                    Sil
                  </button>
                )}
              </div>

              <div className="col-12 field">
                <input
                  value={exp.company}
                  onChange={(e) =>
                    setExperience(exp.id, "company", e.target.value)
                  }
                  placeholder="İş Yeri Adı"
                />
              </div>

              <div className="col-12 field">
                <input
                  value={exp.role}
                  onChange={(e) =>
                    setExperience(exp.id, "role", e.target.value)
                  }
                  placeholder="Göreviniz"
                />
              </div>

              <div className="col-6 field">
                <input
                  type="date"
                  value={exp.start}
                  onChange={(e) =>
                    setExperience(exp.id, "start", e.target.value)
                  }
                  placeholder="Giriş"
                />
              </div>

              <div className="col-6 field">
                <input
                  type="date"
                  value={exp.end}
                  onChange={(e) =>
                    setExperience(exp.id, "end", e.target.value)
                  }
                  placeholder="Çıkış"
                />
              </div>

              <div className="col-12 field">
                <input
                  value={exp.reason}
                  onChange={(e) =>
                    setExperience(exp.id, "reason", e.target.value)
                  }
                  placeholder="Ayrılma nedeni"
                />
              </div>
            </div>
          ))}
        </div>

        {/* CV ve Onay */}
        <div className="ca-block">
          <h3 className="ca-subtitle">CV’nizi Yükleyiniz</h3>
          <div className="row">
            <div className="col-12 field">
              {/* SADECE PDF */}
              <input type="file" accept="application/pdf,.pdf" onChange={onFile} />
              {form.cv && (
                <div className="file-info">Seçilen dosya: {form.cv.name}</div>
              )}
              {cvError && <div className="file-error">{cvError}</div>}
            </div>

            <div className="col-12 field ca-consent">
              <label>
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setField("consent", e.target.checked)}
                />
                Aşağıda verdiğim bilgilerin doğru olduğunu beyan eder, işe kabul
                edildiğimde aksinin ispatlandığı hâlde iş akdimin ihbarsız ve
                tazminatsız fesihini kabul ederim.
              </label>
            </div>

            <div className="col-12 field ca-consent">
              <label>
                <input
                  type="checkbox"
                  checked={form.kvkkConsent}
                  onChange={(e) => setField("kvkkConsent", e.target.checked)}
                  required
                />
                <Link to="/kvkk" target="_blank" style={{ textDecoration: "underline", color: "#0066cc" }}>
                  KVKK Aydınlatma Metni
                </Link>
                'ni okudum, kabul ediyorum.
              </label>
            </div>

            <div className="col-12">
              <button className="btn-primary" type="submit">
                Gönder
              </button>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
}